<template>
    <div>
        <b-container fluid>
            <h3 class="mt-3 mb-3">Auto Route Assignment</h3>

            <h5 class="mt-4">Global Settings</h5>
            <p class="text-muted" style="font-size: 13px;">
                Turn off to stop the "Route Auto-Assigned — Please Review"
                popup from appearing for everyone, on every client. Routes are
                still auto-assigned and learned in the background either way —
                this only silences the review popup.
            </p>
            <b-form-checkbox
                switch
                :checked="popup_enabled == 1"
                :disabled="!canEdit"
                @change="onTogglePopup"
            >Show "Route Auto-Assigned — Please Review" popup</b-form-checkbox>

            <h5 class="mt-4">Client Control</h5>
            <p class="text-muted" style="font-size: 13px;">
                Turn on to let the system auto-assign a route (via saved
                address history, or Google Geocoding as a fallback) whenever
                staff leave the route blank for this client.
            </p>
            <b-table
                responsive
                small
                outlined
                striped
                hover
                sticky-header="300px"
                :items="clients"
                :fields="client_fields"
            >
                <template v-slot:cell(enable_auto_route_assign)="data">
                    <b-form-checkbox
                        switch
                        :checked="data.item.enable_auto_route_assign == 1"
                        :disabled="!canEdit"
                        @change="onToggleClient(data.item)"
                    />
                </template>
            </b-table>

            <h5 class="mt-4">Address &harr; Route History</h5>
            <p class="text-muted" style="font-size: 13px;">
                Look up a specific order or address to review or correct its
                learned route. The full history is too large to browse — enter
                an order # or address to search.
            </p>

            <div class="row mb-2">
                <div class="col-2">
                    <b-form-input
                        size="sm"
                        type="text"
                        v-model="search_criteria.order_number"
                        placeholder="Order #"
                        @keyup.enter="onSearch"
                    />
                </div>
                <div class="col-3">
                    <b-form-input
                        size="sm"
                        type="text"
                        v-model="search_criteria.keyword"
                        placeholder="Search address / route"
                        @keyup.enter="onSearch"
                    />
                </div>
                <div class="col-2">
                    <b-form-select
                        size="sm"
                        v-model="search_criteria.source"
                        :options="source_filter_options"
                    />
                </div>
                <div class="col-2">
                    <b-form-select
                        size="sm"
                        v-model="search_criteria.reliability"
                        :options="reliability_filter_options"
                    />
                </div>
                <div class="col-2">
                    <b-form-select
                        size="sm"
                        v-model="search_criteria.verified"
                        :options="verified_filter_options"
                    />
                </div>
                <div class="col-1">
                    <b-button
                        size="sm"
                        variant="info"
                        :disabled="!canSearch"
                        @click="onSearch"
                        >Search</b-button
                    >
                </div>
            </div>

            <b-alert v-if="!has_searched" show variant="light">
                Enter an order # or address above and click Search.
            </b-alert>

            <template v-else>
                <div class="summary mb-3" v-if="summary">
                    <b-badge variant="secondary" class="mr-2"
                        >Total: {{ summary.total || 0 }}</b-badge
                    >
                    <b-badge variant="info" class="mr-2"
                        >Google-assigned: {{ summary.google_assigned || 0 }}</b-badge
                    >
                    <b-badge variant="success" class="mr-2"
                        >Staff-corrected: {{ summary.staff_corrected || 0 }}</b-badge
                    >
                    <b-badge variant="warning"
                        >Low confidence: {{ summary.low_confidence || 0 }}</b-badge
                    >
                </div>

                <Pagination
                    :totalRows="total_rows"
                    :perPage="rows_per_page"
                    :onPageChange="onPageChange"
                    :pagingLimit="paging_limit"
                    :currentPage="currentPage"
                    :onPerPageChange="onPerPageChange"
                />

                <b-table
                    responsive
                    small
                    outlined
                    striped
                    hover
                    sticky-header="500px"
                    :items="rows"
                    :fields="row_fields"
                >
                <template v-slot:cell(source)="data">
                    <b-badge
                        :variant="data.item.source === 'staff_corrected' ? 'success' : 'info'"
                        >{{ data.item.source }}</b-badge
                    >
                </template>

                <template v-slot:cell(reliability)="data">
                    <b-badge
                        :variant="
                            data.item.reliability === 'HIGH'
                                ? 'success'
                                : data.item.reliability === 'LOW'
                                ? 'danger'
                                : 'warning'
                        "
                        >{{ data.item.reliability || '-' }}</b-badge
                    >
                </template>

                <template v-slot:cell(verified)="data">
                    {{ data.item.verified == 1 ? 'Yes' : 'No' }}
                </template>

                <template v-slot:cell(route)="data">
                    <div v-if="editing_id === data.item.id" class="d-flex">
                        <b-form-select
                            size="sm"
                            v-model="editing_route"
                            :options="tracking_route_options"
                        />
                        <b-button
                            size="sm"
                            variant="info"
                            class="ml-1"
                            @click="onSaveCorrection(data.item)"
                            >Save</b-button
                        >
                        <b-button
                            size="sm"
                            variant="outline-secondary"
                            class="ml-1"
                            @click="editing_id = null"
                            >Cancel</b-button
                        >
                    </div>
                    <div v-else>
                        {{ data.item.route }}
                        <b-button
                            v-if="canEdit"
                            size="sm"
                            variant="link"
                            @click="onStartEdit(data.item)"
                            >Correct</b-button
                        >
                    </div>
                </template>
            </b-table>

                <Pagination
                    :totalRows="total_rows"
                    :perPage="rows_per_page"
                    :onPageChange="onPageChange"
                    :pagingLimit="paging_limit"
                    :currentPage="currentPage"
                    :onPerPageChange="onPerPageChange"
                />
            </template>
        </b-container>
    </div>
</template>

<script>
import Pagination from "./common/Pagination";
import apiService from "../services/ApiService";
import global_function from "../mixins/global_function";

const client_fields = [
    { key: "client_name", label: "Client" },
    { key: "enable_auto_route_assign", label: "Auto Route Assignment" }
];

const row_fields = [
    { key: "address", label: "Address" },
    { key: "route", label: "Route" },
    { key: "source", label: "Source" },
    { key: "reliability", label: "Reliability" },
    { key: "verified", label: "Staff Verified" },
    { key: "tracking_count", label: "Trackings Using This Address" },
    { key: "updated_at", label: "Last Updated" }
];

export default {
    name: "AutoRouteAssignment",
    inject: [
        "API_HOST",
        "TABLE_LIMIT",
        "PAGING_LIMIT",
        "DEFAULT_ROWS_PER_PAGE"
    ],
    mixins: [apiService, global_function],
    components: {
        Pagination
    },
    data() {
        return {
            loader: false,
            popup_enabled: 1,
            clients: [],
            client_fields: client_fields,
            rows: [],
            row_fields: row_fields,
            summary: null,
            tracking_route_options: [],
            currentPage: 1,
            total_rows: 0,
            rows_per_page: this.DEFAULT_ROWS_PER_PAGE,
            paging_limit: this.PAGING_LIMIT,
            search_criteria: {
                order_number: "",
                source: "",
                reliability: "",
                verified: "",
                keyword: ""
            },
            has_searched: false,
            source_filter_options: [
                { value: "", text: "All Sources" },
                { value: "google", text: "Google" },
                { value: "staff_corrected", text: "Staff Corrected" }
            ],
            reliability_filter_options: [
                { value: "", text: "All Reliability" },
                { value: "HIGH", text: "HIGH" },
                { value: "MEDIUM", text: "MEDIUM" },
                { value: "LOW", text: "LOW" }
            ],
            verified_filter_options: [
                { value: "", text: "All" },
                { value: "1", text: "Verified" },
                { value: "0", text: "Unverified" }
            ],
            editing_id: null,
            editing_route: null
        };
    },
    computed: {
        table_offset: function() {
            return (this.currentPage - 1) * this.rows_per_page;
        },
        canEdit: function() {
            return this.checkPermission(
                this.$store.state.user_obj,
                "Delivery Management",
                "Auto Route Assignment",
                "Edit",
                true
            );
        },
        canSearch: function() {
            return !!(
                this.search_criteria.order_number ||
                this.search_criteria.keyword
            );
        }
    },
    async created() {
        if (
            !this.checkPermission(
                this.$store.state.user_obj,
                "Delivery Management",
                "Auto Route Assignment",
                "View",
                true
            )
        ) {
            this.$router.replace("/");
            return;
        }

        let routes_response = await this.getTrackingRoutes();
        for (let i in routes_response.tracking_routes) {
            this.tracking_route_options.push({
                value: routes_response.tracking_routes[i].route_name,
                text: routes_response.tracking_routes[i].route_name
            });
        }

        await this.getList();
    },
    mounted() {
        this.loader = this.$loading.show({
            isFullPage: true,
            canCancel: false
        });
    },
    methods: {
        async getList() {
            let response = await this.$http({
                method: "POST",
                baseURL: `${this.API_HOST}`,
                url: "address_route_list_get.php",
                data: {
                    page: this.currentPage,
                    limit: this.rows_per_page,
                    search_criteria: this.search_criteria,
                    user_id: this.$store.state.user_obj.user_id
                }
            });

            this.clients = response.data.clients;
            this.popup_enabled = response.data.popup_enabled;
            this.rows = response.data.rows;
            this.summary = response.data.summary;
            this.total_rows = response.data.rows.length
                ? parseInt(response.data.summary.total)
                : 0;

            if (this.loader) {
                this.loader.hide();
            }
        },

        onSearch() {
            if (!this.canSearch) {
                return;
            }
            this.has_searched = true;
            this.currentPage = 1;
            this.getList();
        },

        onPageChange(page_to_change) {
            this.currentPage = page_to_change;
            this.getList();
        },

        onPerPageChange(_perPage) {
            this.currentPage = 1;
            this.rows_per_page = _perPage;
            this.getList();
        },

        async onToggleClient(client_item) {
            let new_value = client_item.enable_auto_route_assign == 1 ? 0 : 1;

            await this.$http({
                method: "POST",
                baseURL: `${this.API_HOST}`,
                url: "client_auto_route_toggle.php",
                data: {
                    client_id: client_item.id,
                    enabled: new_value,
                    user_id: this.$store.state.user_obj.user_id
                }
            });

            client_item.enable_auto_route_assign = new_value;
        },

        async onTogglePopup() {
            let new_value = this.popup_enabled == 1 ? 0 : 1;

            await this.$http({
                method: "POST",
                baseURL: `${this.API_HOST}`,
                url: "auto_route_popup_toggle.php",
                data: {
                    enabled: new_value,
                    user_id: this.$store.state.user_obj.user_id
                }
            });

            this.popup_enabled = new_value;
        },

        onStartEdit(row_item) {
            this.editing_id = row_item.id;
            this.editing_route = row_item.route;
        },

        async onSaveCorrection(row_item) {
            if (!this.editing_route || this.editing_route === row_item.route) {
                this.editing_id = null;
                return;
            }

            await this.$http({
                method: "POST",
                baseURL: `${this.API_HOST}`,
                url: "address_route_correct.php",
                data: {
                    id: row_item.id,
                    route: this.editing_route,
                    user_id: this.$store.state.user_obj.user_id
                }
            });

            this.editing_id = null;
            await this.getList();
        }
    }
};
</script>

<style>
th,
td {
    white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/components/AutoRouteAssignment.vue